import { inject, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { PARAMETER_TYPE_INTERNAL } from '../../__legacy__/helpers/parameters.helpers';
import { PaginationInfoDefault } from '../../__legacy__/models/paginationInfo.model';
import { IOverlayLoaderStore } from '../common/OverlayLoader.store';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IJobDetailsService } from './jobDetails.service';
import { INFERENCE_TYPE, JOB_TYPE, TRAINING_TYPE, TRAINING_VARIANT } from './jobs.model';
import { IJobDetailsApiService } from './services/jobDetailsApi.service';

interface IToggleOptions {
  addative: boolean;
}

interface IToggleAllOptions {
  forceAll: boolean;
}

export interface IJobDetailsFormsBl {
  //Settings
  handleChangeNumericParam: (label: string, value: string) => void;
  handleBlurNumericParam: (label: string, value: string) => void;
  handleChangeBooleanParam: (label: string, value: boolean) => void;
  //Header
  handleChangeName: (name: string) => void;
  handleBlurNameAsync: (name: string) => Promise<void>;
  handleChangeDescription: (description: string) => void;
  handleBlurDescriptionAsync: (description: string) => Promise<void>;
  handleChangeProjectAsync: (projectId: string) => Promise<void>;
  handleBlurProject: (projectId: string) => void;
  handleChangeTraining: (trainingId: string) => void;
  handleBlurTraining: (trainingId: string) => void;
  handleChangeTrainingVariantAsync: (trainingVariant: TRAINING_VARIANT | null) => Promise<void>;
  handleBlurTrainingVariant: (trainingVariant: TRAINING_VARIANT | null) => void;
  handleChangeJobType: (type: INFERENCE_TYPE | TRAINING_TYPE) => Promise<void>;
  //Datasets
  toggleDatasetAsync: (datasetId: string, options: IToggleOptions) => Promise<void>;
  handleBlurDataset: (datasetId: string) => void;
  toggleAllDatasetsAsync(options: IToggleAllOptions): Promise<void>;
  // Annotations
  toggleAnnotationTool(toolId: string): void;
  toggleAllAnnotationTools(options: IToggleAllOptions): void;
}

export namespace IJobDetailsFormsBl {
  export const $: interfaces.ServiceIdentifier<IJobDetailsFormsBl> = Symbol('JOB_DETAILS_FORMS_BL_TYPE');
}

export class JobDetailsFormsBl implements IJobDetailsFormsBl {
  constructor(
    @inject(IJobDetailsService.$) public readonly jobDetailsService: IJobDetailsService,
    @inject(IJobDetailsApiService.$) private readonly jobDetailsApiService: IJobDetailsApiService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore
  ) {
    makeObservable(this);
  }

  @action.bound
  handleChangeNumericParam = (name: string, value: string): void => {
    const {
      job: { config },
      params,
    } = this.jobDetailsService.store;

    const param = params.find((param) => param.name === name);

    if (!param) {
      return;
    }

    const { type } = param;
    const asInt = parseInt(value, 10);
    const asFloat = parseFloat(value);

    if (isNaN(asInt) || isNaN(asFloat)) {
      this.jobDetailsService.store.job.config = { ...config, [name]: null };
      return;
    }

    if (type === PARAMETER_TYPE_INTERNAL.INT) {
      this.jobDetailsService.store.job.config = { ...config, [name]: asInt };
    } else if (type === PARAMETER_TYPE_INTERNAL.FLOAT) {
      this.jobDetailsService.store.job.config = { ...config, [name]: asFloat };
    }
  };

  @action
  handleBlurNumericParam = (name: string, value: string): void => {
    const { validationErrors, params } = this.jobDetailsService.store;
    const param = params.find((param) => param.name === name);
    if (!param || (param.type !== PARAMETER_TYPE_INTERNAL.FLOAT && param.type !== PARAMETER_TYPE_INTERNAL.INT)) {
      return;
    }

    const validationResult = param.config.validator(value || null);
    if (validationResult) {
      validationErrors.settings.set(param.name, null);
    } else {
      validationErrors.settings.set(param.name, {
        message: 'params.numeric_validation_error',
        messageParameters: {
          rangeStart: param.config.min,
          rangeEnd: param.config.max,
        },
      });
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action
  handleChangeBooleanParam = (label: string, value: boolean): void => {
    this.jobDetailsService.store.job.config[label] = value;
  };

  @action
  handleChangeName = (name: string): void => {
    this.jobDetailsService.store.job.name = name;
  };

  @action
  handleBlurNameAsync = async (name: string): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
      validationErrors,
    } = this.jobDetailsService.store;

    if (!name) {
      validationErrors.header.set('name', {
        message: 'fields.text_validation_error',
      });

      return;
    }

    validationErrors.header.set('name', null);
    if (id) {
      await this.jobDetailsApiService.postJobInfoEditAsync(currentWorkspace.id, id, jobType, { name });
    }
    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action
  handleChangeDescription = (description: string): void => {
    this.jobDetailsService.store.job.description = description;
  };

  @action
  handleBlurDescriptionAsync = async (description: string): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
    } = this.jobDetailsService.store;

    if (id) {
      await this.jobDetailsApiService.postJobInfoEditAsync(currentWorkspace.id, id, jobType, { description });
    }
  };

  @action
  handleChangeProjectAsync = async (projectId: string): Promise<void> => {
    this.jobDetailsService.store.job.projectId = projectId;
    this.jobDetailsService.store.validationErrors.custom = new Map();
    this.overlayLoaderStore.enableLoader('job-header');
    this.overlayLoaderStore.enableLoader('job-datasets-table');
    this.overlayLoaderStore.enableLoader('job-annotation-tools-table');
    this.overlayLoaderStore.enableLoader('job-images-loader');

    if (this.jobDetailsService.store.jobType === JOB_TYPE.TRAINING) {
      const trainingVariant =
        this.jobDetailsService.store.dataSources.projects.find((project) => project.value === projectId)?.modelVariant ??
        this.jobDetailsService.store.job.variant;
      if (trainingVariant) {
        await this.handleChangeTrainingVariantAsync(trainingVariant);
      }
    }

    const promises = [
      this.jobDetailsService.store.job.variant !== null
        ? this.jobDetailsService.populateQuestionsAsync()
        : Promise.resolve(() => {
            this.overlayLoaderStore.disableLoader('job-annotation-tools-table');
          }),
      this.jobDetailsService.populateDatasetsAsync(),
    ];
    await Promise.allSettled(promises);
    await this.toggleAllDatasetsAsync({ forceAll: true });
    this.overlayLoaderStore.disableLoader('job-header');
  };

  @action
  handleBlurProject = (projectId: string): void => {
    const {
      job: { datasetIds },
      validationErrors,
    } = this.jobDetailsService.store;

    if (!projectId) {
      validationErrors.header.set('project', {
        message: 'fields.text_validation_error',
      });
      validationErrors.datasets = true;
    } else {
      validationErrors.header.set('project', null);
    }

    if (datasetIds.length) {
      validationErrors.datasets = false;
      validationErrors.images = false;
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action
  handleChangeTraining = (trainingId: string): void => {
    this.jobDetailsService.store.job.trainingId = trainingId;
  };

  @action
  handleBlurTraining = (trainingId: string): void => {
    const { validationErrors } = this.jobDetailsService.store;

    if (!trainingId) {
      validationErrors.header.set('training', {
        message: 'fields.text_validation_error',
      });
    } else {
      validationErrors.header.set('training', null);
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action
  handleChangeTrainingVariantAsync = async (trainingVariant: TRAINING_VARIANT | null): Promise<void> => {
    const oldVariant = this.jobDetailsService.store.job.variant;
    this.jobDetailsService.store.job.variant = trainingVariant;
    const headerLoaderVisible = this.overlayLoaderStore.isSpinnerVisible('job-header');
    this.overlayLoaderStore.enableLoader('job-header');
    this.overlayLoaderStore.enableLoader('job-settings-list');
    this.overlayLoaderStore.enableLoader('job-annotation-tools-table');
    this.jobDetailsService.store.job.trainingId = '';
    const {
      validationErrors,
      job: { projectId },
      jobType,
    } = this.jobDetailsService.store;
    if (validationErrors.showAll) {
      validationErrors.header.set('variant', null);
    } else {
      validationErrors.header.delete('variant');
    }
    validationErrors.settings.clear();
    await Promise.allSettled([
      jobType === JOB_TYPE.INFERENCE ? this.jobDetailsService.populateTrainings() : Promise.resolve(),
      this.jobDetailsService.populateJobSettingsWithDefaultsAsync(),
      projectId && trainingVariant !== oldVariant ? this.jobDetailsService.populateQuestionsAsync() : Promise.resolve(),
    ]);
    if (!headerLoaderVisible) {
      this.overlayLoaderStore.disableLoader('job-header');
    }
    this.overlayLoaderStore.disableLoader('job-settings-list');
  };

  @action
  handleBlurTrainingVariant = (modelVariant: TRAINING_VARIANT | null): void => {
    const { validationErrors } = this.jobDetailsService.store;

    if (!modelVariant) {
      validationErrors.header.set('variant', {
        message: 'fields.text_validation_error',
      });
    } else {
      validationErrors.header.set('variant', null);
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action.bound
  async handleChangeJobType(type: INFERENCE_TYPE | TRAINING_TYPE): Promise<void> {
    this.jobDetailsService.store.job.type = type;
    const {
      validationErrors,
      params,
      job: { config },
    } = this.jobDetailsService.store;

    // Inference specific handling
    if (this.jobDetailsService.store.jobType === JOB_TYPE.INFERENCE) {
      this.jobDetailsService.store.job = {
        ...this.jobDetailsService.store.job,
        type: type as INFERENCE_TYPE,
        datasetIds: [],
        projectId: '',
      };
      this.jobDetailsService.store.dataSources.datasets = [];
      this.jobDetailsService.store.images = {
        ...this.jobDetailsService.store.images,
        images: [],
      };

      if (type === INFERENCE_TYPE.DATASET) {
        await this.jobDetailsService.populateWorkspaceDatasetsAsync();
      } else {
        await this.jobDetailsService.populateProjectsAsync();
      }
    }

    // Handle errors
    switch (type) {
      case TRAINING_TYPE.AUTO:
        validationErrors.settings.clear();
        break;
      case TRAINING_TYPE.MANUAL:
        params.forEach((param) => {
          if (param.type !== PARAMETER_TYPE_INTERNAL.BOOL) {
            this.handleBlurNumericParam(param.label, config[param.name] as string);
          }
        });
        break;
      case INFERENCE_TYPE.DATASET:
        if (validationErrors.showAll) {
          validationErrors.header.delete('project');
          validationErrors.header.set('dataset', {
            message: 'fields.text_validation_error',
          });
        }
        validationErrors.datasets = undefined;
        validationErrors.images = undefined;
        break;
      case INFERENCE_TYPE.PROJECT:
        if (validationErrors.showAll) {
          validationErrors.header.set('project', {
            message: 'fields.text_validation_error',
          });
          validationErrors.header.delete('dataset');
        }
        validationErrors.datasets = undefined;
        validationErrors.images = undefined;
        break;
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  }

  @action.bound
  toggleAnnotationTool(toolId: string): void {
    const {
      job: { annotationToolIds },
      validationErrors,
    } = this.jobDetailsService.store;

    if (annotationToolIds.includes(toolId)) {
      this.jobDetailsService.store.job.annotationToolIds = annotationToolIds.filter((id) => id !== toolId);
    } else {
      this.jobDetailsService.store.job.annotationToolIds = [...this.jobDetailsService.store.job.annotationToolIds, toolId];
    }

    if (!validationErrors.showAll) {
      return;
    }

    validationErrors.annotationTools = this.jobDetailsService.store.job.annotationToolIds.length === 0;
    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  }

  @action.bound
  toggleAllAnnotationTools({ forceAll }: IToggleAllOptions): void {
    const { validationErrors } = this.jobDetailsService.store;
    this.jobDetailsService.store.job.annotationToolIds = [];

    if (forceAll || this.jobDetailsService.store.job.annotationToolIds.length === 0) {
      this.jobDetailsService.store.job.annotationToolIds = this.jobDetailsService.store.annotationTools
        .filter((annotationTool) => annotationTool.compatible)
        .map((annotationTool) => annotationTool.id);
    } else {
      this.jobDetailsService.store.job.annotationToolIds = this.jobDetailsService.store.annotationTools
        .filter((annotationTool) => annotationTool.required)
        .map((annotationTool) => annotationTool.id);
    }

    if (!validationErrors.showAll) {
      return;
    }

    validationErrors.annotationTools = this.jobDetailsService.store.job.annotationToolIds.length === 0;
    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  }

  @action
  toggleDatasetAsync = async (datasetId: string, { addative }: IToggleOptions): Promise<void> => {
    const {
      job: { datasetIds, type },
      dataSources: { datasetsInWorkspace },
      validationErrors,
    } = this.jobDetailsService.store;

    if (datasetIds.includes(datasetId)) {
      this.jobDetailsService.store.job.datasetIds = datasetIds.filter((id) => id !== datasetId);
    } else {
      if (addative) {
        this.jobDetailsService.store.job.datasetIds = [...datasetIds, datasetId];
      } else {
        this.jobDetailsService.store.job.datasetIds = [datasetId];
        // In case of inference on dataset we have to add it to dataSource so it's properly diplayed
        if (type === INFERENCE_TYPE.DATASET) {
          this.jobDetailsService.store.dataSources.datasets = datasetsInWorkspace.filter((dataset) => dataset.id === datasetId);
        }
      }
    }

    const hasError = this.jobDetailsService.store.job.datasetIds.length === 0;

    validationErrors.datasets = hasError;
    validationErrors.images = hasError;
    this.jobDetailsService.store.validationErrors = { ...validationErrors };
    await this.jobDetailsService.changePaginationAsync(PaginationInfoDefault.pageNumber, this.jobDetailsService.store.images.paging.pageSize);
  };

  @action
  handleBlurDataset = (datasetId: string): void => {
    const {
      job: { datasetIds },
      validationErrors,
    } = this.jobDetailsService.store;

    if (!datasetId) {
      validationErrors.header.set('dataset', {
        message: 'fields.text_validation_error',
      });
      validationErrors.datasets = true;
    } else {
      validationErrors.header.set('dataset', null);
    }

    if (datasetIds.length) {
      validationErrors.datasets = false;
      validationErrors.images = false;
    }

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
  };

  @action.bound
  async toggleAllDatasetsAsync({ forceAll }: IToggleAllOptions): Promise<void> {
    if (forceAll || this.jobDetailsService.store.dataSources.datasets.length !== this.jobDetailsService.store.job.datasetIds.length) {
      this.jobDetailsService.store.job.datasetIds = this.jobDetailsService.store.dataSources.datasets.map((dataset) => dataset.id);
    } else {
      this.jobDetailsService.store.job.datasetIds = [];
    }
    const { validationErrors } = this.jobDetailsService.store;
    const hasError = this.jobDetailsService.store.job.datasetIds.length === 0;
    validationErrors.datasets = hasError;
    validationErrors.images = hasError;

    this.jobDetailsService.store.validationErrors = { ...validationErrors };
    await this.jobDetailsService.changePaginationAsync(PaginationInfoDefault.pageNumber, this.jobDetailsService.store.images.paging.pageSize);
  }
}
